import graphql__93db0a464611a09df831 from "./__generated__/ListContribution_contribution.graphql.js";import { useEffect, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { defineMessages } from "react-intl";
import classNames from "classnames";
import { animateScroll as scroll } from "react-scroll";

import ContributionThemesLabel from "./ContributionThemesLabel";
import ContributionContent from "../../Contributions/ContributionContent";
import ContributionCommentsPanel from "./ContributionCommentsPanel";
import ContributionBookmarkButton from "./ContributionBookmarkButton";
import VoteButtons from "../../Votable/VoteButtons";
import Tooltip from "../../Tooltip/Tooltip";
import Icon from "../../Utils/Icon";
import Report from "../../Reports/Report";
import { Desktop, NonDesktop, useResponsiveFlag } from "../../Utils/ResponsiveQueries";
import { isResourcesListItemSelected } from "../../Utils/resourcesList";
import RichText from "../../Utils/RichText";
import AnswerFormField from "../../Answer/AnswerFormField";
import { useFormatMessage } from "../../Intl/Context";
import { isUnauthorized } from "../../Utils/authorizations";
import { useHighlightSearch, useResourcesList } from "../../ResourcesList/resourcesList";
import { useGetStickyElementsHeight } from "../../Utils/StickyElementsHeight";
import ReactableMeasures from "./ReactableMeasures";
import ContributionHeader from "../../Contributions/ContributionHeader";

// This is a fixed offset to allow the scrolling of the page to land just before the
// container of the contribution, when scrolling the page after a contribution is selected
// from the contributions navigation list.
const CONTRIBUTION_GUTTER_OFFSET = 8;

const ListContribution = props => {
  const contribution = useFragment(ListContribution_contribution, props.contribution);

  const resourcesList = useResourcesList();
  const isSelected = isResourcesListItemSelected(resourcesList, {
    id: contribution.id,
    type: "contribution"
  });
  const highlightSearch = useHighlightSearch();

  const { isNonDesktop } = useResponsiveFlag();

  const searchTerms = resourcesList.search.terms;

  const getStickyElementsHeight = useGetStickyElementsHeight();

  const { isFeatured, themes, isFromAdminUser } = contribution;
  const {
    votesDisplayAuthorized,
    votesSubmissionAuthorized,
    commentsDisplayAuthorized,
    commentsSubmissionAuthorized
  } = contribution.authorizations;

  const t = useFormatMessage();
  const featuredContributionLabel = t(intlMessages.featuredContribution);

  const laureateContribution = t(intlMessages.laureateContribution);

  const [container, setContainer] = useState();

  // If the contribution was just marked as selected, scroll the window to
  // its location. Note that this callback is only updated when the `isSelected` prop
  // changes, as the other useEffect dependencies never change.
  //
  useEffect(() => {
    if (container && isSelected) {
      const { top } = container.getBoundingClientRect();
      const { scrollY } = window;
      const stickyElementsHeight = getStickyElementsHeight();
      const scrollTop = top + scrollY - stickyElementsHeight - CONTRIBUTION_GUTTER_OFFSET;

      scroll.scrollTo(scrollTop, { duration: 300 });
      container.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelected, container]);

  const { additionalDisplayableFields } = contribution;

  const displayVotesMetrics =
    !isUnauthorized(votesDisplayAuthorized) && isUnauthorized(votesSubmissionAuthorized);

  const className = classNames("contribution", "list-contribution", "position-relative", {
    "contribution-featured": isFeatured,
    "border-primary": isFeatured,
    // Rounded borders are handled by .contribution-featured and, on mobile, the box has
    // an increased top margin that would display a gray spacer if we don't force the
    // whole block's background to be white
    "bg-white": isFeatured
  });

  const innerBoxClassName = classNames(
    "box",
    "padding-lg",
    "bg-white",
    "contribution-inner",
    { "margin-top-md": isFeatured && isNonDesktop },
    { "admin-highlight": isFromAdminUser }
  );

  const footerActionsClassName = classNames("d-flex", {
    "justify-content-between": displayVotesMetrics,
    "justify-content-end": !displayVotesMetrics
  });

  return (
    <article className={className} ref={setContainer} tabIndex="-1">
      {isFeatured && (
        <Tooltip content={laureateContribution}>
          <div className="contribution-featured-label bg-primary d-flex align-items-center position-absolute padding-xxs">
            <Icon className="fa fa-trophy text-primary text-number-lg" />
            {featuredContributionLabel}
          </div>
        </Tooltip>
      )}
      <Desktop>
        <VoteButtons votable={contribution} className="contribution-vote-buttons" />
      </Desktop>

      <div className={innerBoxClassName}>
        <ContributionHeader contribution={contribution} className="margin-bottom-sm" />

        {contribution.title && (
          <h4 className="text-subtitle-contribution margin-bottom-sm">
            {highlightSearch(contribution.title)}
          </h4>
        )}

        <ContributionThemesLabel themes={themes} className="margin-bottom-sm" />

        <RichText className="text-default padding-none margin-bottom-md">
          <ContributionContent contribution={contribution} highlightTerms={searchTerms} />
        </RichText>

        {additionalDisplayableFields.map(field => (
          <AnswerFormField
            key={field.id}
            field={field}
            className="contribution-answer-form-field"
          />
        ))}

        {isUnauthorized(commentsDisplayAuthorized) && (
          <div className={footerActionsClassName}>
            {displayVotesMetrics && (
              <ReactableMeasures
                reactable={contribution}
                hideComments={isUnauthorized(commentsSubmissionAuthorized)}
              />
            )}

            <div className="float-right d-flex align-items-center margin-bottom-xxs">
              <ContributionBookmarkButton
                contribution={contribution}
                displayLabel={false}
                ignoreDefaultClassName={true}
                className="btn btn-unstyled padding-xxs margin-right-xxs"
              />

              <Report reportable={contribution} />
            </div>
          </div>
        )}

        <NonDesktop>
          <VoteButtons votable={contribution} className="margin-bottom-md" />
        </NonDesktop>

        {!isUnauthorized(commentsDisplayAuthorized) && (
          <ContributionCommentsPanel
            contribution={contribution}
            commentableId={contribution.id}
          />
        )}
      </div>
    </article>
  );
};

const ListContribution_contribution = graphql__93db0a464611a09df831;

export default ListContribution;

const intlMessages = defineMessages({
  featuredContribution: {
    id: "contribution.card.featuredContribution",
    defaultMessage: "Contribution sélectionnée"
  },
  laureateContribution: {
    id: "contribution.card.laureateContribution",
    defaultMessage: "Cette proposition est lauréate de l'appel à projets"
  }
});
